import { authFetch, API_URL, clearAuthSession, getAuthHeaders } from "../../utils/auth";

const CATEGORY_URL = `${API_URL}/inventory-categories/`;
const ITEM_URL = `${API_URL}/inventory-items/`;
const TRANSACTION_URL = `${API_URL}/inventory-transactions/`;
const SUMMARY_URL = `${API_URL}/inventory-summary/`;

function authHeaders() {
  return {
    Accept: "application/json",
    ...getAuthHeaders(),
  };
}

function jsonHeaders() {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...getAuthHeaders(),
  };
}

function normalizeList(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
}

function firstError(data) {
  if (!data || typeof data !== "object") {
    return "";
  }

  for (const [field, value] of Object.entries(data)) {
    if (Array.isArray(value) && value.length > 0) {
      const nested = firstError(value[0]);

      return nested || `${field}: ${String(value[0])}`;
    }

    if (value && typeof value === "object") {
      const nested = firstError(value);

      if (nested) {
        return `${field}: ${nested}`;
      }
    }

    if (typeof value === "string") {
      return `${field}: ${value}`;
    }
  }

  return "";
}

async function parseResponse(response) {
  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await response.json()
    : { detail: "The request could not be completed." };

  if (response.status === 401) {
    clearAuthSession();
  }

  if (!response.ok) {
    const error = new Error(
      data?.detail ||
        data?.message ||
        firstError(data) ||
        "The inventory request could not be completed.",
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

function buildQuery(filters = {}) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });

  const query = params.toString();

  return query ? `?${query}` : "";
}

export async function fetchInventoryCategories({ active = "", signal } = {}) {
  const data = await parseResponse(
    await authFetch(`${CATEGORY_URL}${buildQuery({ active })}`, {
      headers: authHeaders(),
      signal,
    }),
  );

  return normalizeList(data);
}

export async function createInventoryCategory(payload) {
  return parseResponse(
    await authFetch(CATEGORY_URL, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }),
  );
}

export async function updateInventoryCategory(categoryId, payload) {
  return parseResponse(
    await authFetch(`${CATEGORY_URL}${categoryId}/`, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }),
  );
}

export async function fetchInventoryItems(filters = {}) {
  const { signal, ...query } = filters;
  const data = await parseResponse(
    await authFetch(`${ITEM_URL}${buildQuery(query)}`, {
      headers: authHeaders(),
      signal,
    }),
  );

  return normalizeList(data);
}

export async function createInventoryItem(payload) {
  return parseResponse(
    await authFetch(ITEM_URL, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }),
  );
}

export async function updateInventoryItem(itemId, payload) {
  return parseResponse(
    await authFetch(`${ITEM_URL}${itemId}/`, {
      method: "PATCH",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }),
  );
}

export async function adjustInventoryItem(itemId, payload) {
  return parseResponse(
    await authFetch(`${ITEM_URL}${itemId}/adjust/`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify(payload),
    }),
  );
}

export async function fetchInventoryItemTransactions(itemId, { signal } = {}) {
  const data = await parseResponse(
    await authFetch(`${ITEM_URL}${itemId}/transactions/`, {
      headers: authHeaders(),
      signal,
    }),
  );

  return normalizeList(data);
}

export async function fetchInventoryTransactions(filters = {}) {
  const { signal, ...query } = filters;
  const data = await parseResponse(
    await authFetch(`${TRANSACTION_URL}${buildQuery(query)}`, {
      headers: authHeaders(),
      signal,
    }),
  );

  return normalizeList(data);
}

export async function fetchInventorySummary({ signal } = {}) {
  return parseResponse(
    await authFetch(SUMMARY_URL, {
      headers: authHeaders(),
      signal,
    }),
  );
}
